import {
  CrownOutlined,
  DollarCircleOutlined,
  FundOutlined,
  HistoryOutlined,
  MailOutlined,
  SlidersOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { PageHeader } from '@organisms';
import { Col, Row, Tabs } from 'antd';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { ContentComponent } from 'src/app/components/ContentComponent';

import { Actions } from '../components/Actions';
import { CustomerBenefitsTab } from '../components/CustomerBenefits';
import { CustomerCommunicationTab } from '../components/CustomerCommunication';
import { CustomerEngagementTab } from '../components/CustomerEngagement';
import { CustomerFinancialTab } from '../components/CustomerFinancial';
import { CustomerFinancialSummaryTab } from '../components/CustomerFinancialSummary';
import { CustomerHistory } from '../components/CustomerHistory';
import { CustomerSummaryTab } from '../components/CustomerSummary';
import { EmptyState } from '../components/EmptyState';
import { IconTitle } from '../components/IconTitle';
import { InputSearch, InputSearchData } from '../components/SearchInput';
import { useCustomerDetails } from '../hooks/useCustomerDetails';

export const CustomerDetailsScreen: React.FC = () => {
  const {
    tabActive,
    setTabActive,
    customerData,
    customerExists,
    handleSearchCustomer,
    isLoading,
  } = useCustomerDetails();

  const navigate = useNavigate();

  const items = useMemo(() => {
    if (!customerData || !customerExists) return [];

    return [
      {
        label: <IconTitle icon={<UserOutlined />} text='Resumo do Cliente' />,
        key: '0',
        children: (
          <CustomerSummaryTab
            customerData={customerData}
            setTabActive={setTabActive}
          />
        ),
      },
      {
        label: <IconTitle icon={<DollarCircleOutlined />} text='Financeiro' />,
        key: '1',
        children: (
          <CustomerFinancialTab
            customerDocument={customerData.customer.document}
            customerEmail={customerData.customer.email}
            customerId={customerData.customer.id}
          />
        ),
      },
      {
        label: (
          <IconTitle icon={<DollarCircleOutlined />} text='Resumo financeiro' />
        ),
        key: '6',
        children: (
          <CustomerFinancialSummaryTab customer={customerData.customer} />
        ),
      },
      {
        label: <IconTitle icon={<CrownOutlined />} text='Benefícios' />,
        key: '7',
        children: (
          <CustomerBenefitsTab
            enabled={tabActive === '7'}
            customerData={customerData}
          />
        ),
      },
      {
        label: <IconTitle icon={<MailOutlined />} text='Comunicações' />,
        key: '2',
        children: <CustomerCommunicationTab customerData={customerData} />,
      },
      {
        label: <IconTitle icon={<FundOutlined />} text='Uso e Engajamento' />,
        key: '3',
        children: <CustomerEngagementTab customerData={customerData} />,
      },
      {
        label: <IconTitle icon={<SlidersOutlined />} text='Ações' />,
        key: '4',
        children: <Actions customerData={customerData.customer} />,
      },
      {
        label: <IconTitle icon={<HistoryOutlined />} text='Histórico' />,
        key: '5',
        children: <CustomerHistory customerId={customerData.customer.id} />,
      },
    ];
  }, [customerData, customerExists, setTabActive, tabActive]);

  return (
    <ContentComponent isError={false} isLoading={isLoading}>
      <PageHeader
        backToPath={() => {
          navigate(-1);
        }}
        title={!customerExists ? 'Cliente' : customerData?.customer.name || ''}
        subTitle={'Cadastre, edite e configure dados do cliente'}
        extra={
          <InputSearch
            onSearch={(inputData: InputSearchData) =>
              handleSearchCustomer(inputData.search)
            }
          />
        }
        hasDivider={false}
      />
      {customerData && (
        <CustomTab
          items={items}
          activeKey={tabActive}
          onChange={setTabActive}
        />
      )}

      {!customerExists && (
        <Row
          justify='center'
          align='middle'
          style={{ width: '100%', marginTop: '64px' }}
        >
          <Col>
            <EmptyState
              title='Cliente não encontrado.'
              subtitle='Faça a pesquisa novamente utilizando id, ou email do cliente.'
            />
          </Col>
        </Row>
      )}
    </ContentComponent>
  );
};

const CustomTab = styled(Tabs)`
  margin-top: 32px;
  margin-bottom: 0px;
  .ant-tabs-nav {
    margin: 0;
  }
`;
